import { ChevronDown, RotateCcw } from 'lucide-react'
import IndicatorConfigModal from '@/components/Indicators/IndicatorConfigModal'
import ColorPickerField from '@/components/Indicators/ColorPickerField'
import SliderField from '@/components/Indicators/SliderField'
import ToggleField from '@/components/Indicators/ToggleField'
import { higherTimeframes, sanitizeHtfTimeframes, mtfLabel, MTF_MAX_ADDITIONAL } from '@/utils/mtf'
import { TIMEFRAMES } from '@/constants/timeframes'
import { defaultConfig } from './index'

function Section({ title, children, sectionKey, config, onChange }) {
  const open = config?.panelSections?.[sectionKey] ?? false
  const toggle = () => onChange({ ...config, panelSections: { ...(config.panelSections || {}), [sectionKey]: !open } })
  return (
    <div className="border border-[var(--hair,rgba(176,141,87,0.35))] rounded-lg overflow-hidden">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between px-2.5 py-2 bg-[var(--paper,#FDFDFA)] hover:bg-black/[0.02] transition-colors"
      >
        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--gold,#B08D57)]">{title}</span>
        <ChevronDown size={14} className={`text-[var(--ink-soft,#4A5A52)] transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && (
        <div className="px-2.5 py-2 space-y-2 bg-white/50 dark:bg-gray-900/20">
          {children}
        </div>
      )}
    </div>
  )
}

const STYLE_KEYS = [
  'colorBull', 'colorBear',
  'fillOpacity', 'borderWidth',
]

const LOGIC_KEYS = [
  'detectionMode', 'swingLength', 'useBody', 'maxVisibleZones',
  'showMitigated', 'showBreakers', 'chopControl', 'mitigation',
]

export default function OrderBlocksQuantumPanel({ config, onChange, onClose, chartTimeframe }) {
  const cfg = config || {}

  const update = (key, value) => onChange({ ...cfg, [key]: value })

  // MTF: solo se ofrecen TFs estrictamente mayores que el del chart; la
  // selección se sanea contra el chart actual (un TF que deja de ser mayor
  // desaparece de los chips y deja de pedirse, sin borrar la config).
  const htfOptions = higherTimeframes(chartTimeframe)
  const htfSelected = sanitizeHtfTimeframes(cfg.htfTimeframes ?? [], chartTimeframe)
  const chartTfLabel = TIMEFRAMES.find((t) => t.value === chartTimeframe)?.label ?? chartTimeframe
  const toggleHtf = (tf) => {
    if (htfSelected.includes(tf)) {
      update('htfTimeframes', htfSelected.filter((t) => t !== tf))
    } else if (htfSelected.length < MTF_MAX_ADDITIONAL) {
      update('htfTimeframes', [...htfSelected, tf])
    }
  }

  const resetStyle = () => {
    const patch = {}
    for (const k of STYLE_KEYS) patch[k] = defaultConfig[k]
    onChange({ ...cfg, ...patch })
  }

  return (
    <IndicatorConfigModal title="Configuración Order Blocks Quantum" isOpen onClose={onClose}>
      <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">

        <Section title="Zonas" sectionKey="zones" config={cfg} onChange={onChange}>
          <ToggleField label="OB alcistas (OB+)" value={cfg.showBullOB ?? true} onChange={(v) => update('showBullOB', v)} />
          <ToggleField label="OB bajistas (OB−)" value={cfg.showBearOB ?? true} onChange={(v) => update('showBearOB', v)} />
          <ToggleField label="Etiquetas" value={cfg.showLabels ?? true} onChange={(v) => update('showLabels', v)} />
        </Section>

        <Section title="Lógica" sectionKey="logic" config={cfg} onChange={onChange}>
          <div className="space-y-1">
            <span className="text-xs text-[var(--ink-soft,#4A5A52)]">Modo de detección</span>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'standard', label: 'Estándar' },
                { value: 'scalping', label: 'Scalping' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => update('detectionMode', opt.value)}
                  className={`px-2 py-1.5 text-[11px] font-medium rounded-lg border transition-colors ${
                    (cfg.detectionMode ?? 'standard') === opt.value
                      ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300'
                      : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-400 hover:bg-slate-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <SliderField label="Swing length" value={cfg.swingLength ?? 5} min={1} max={50} step={1} unit=" velas" onChange={(v) => update('swingLength', v)} />
          <ToggleField label="Usar cuerpo de vela" value={cfg.useBody ?? false} onChange={(v) => update('useBody', v)} />
          <SliderField label="Máx. bloques por lado" value={cfg.maxVisibleZones ?? 5} min={1} max={20} step={1} onChange={(v) => update('maxVisibleZones', v)} />
          <ToggleField label="Mostrar mitigados" value={cfg.showMitigated ?? true} onChange={(v) => update('showMitigated', v)} />
          <ToggleField label="Mostrar breakers" value={cfg.showBreakers ?? false} onChange={(v) => update('showBreakers', v)} />
          <ToggleField label="Control de chop" value={cfg.chopControl ?? true} onChange={(v) => update('chopControl', v)} />
          <div className="space-y-1">
            <span className="text-xs text-[var(--ink-soft,#4A5A52)]">Mitigación</span>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'wick', label: 'Wick' },
                { value: 'close', label: 'Close' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => update('mitigation', opt.value)}
                  className={`px-2 py-1.5 text-[11px] font-medium rounded-lg border transition-colors ${
                    (cfg.mitigation ?? 'wick') === opt.value
                      ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300'
                      : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-400 hover:bg-slate-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1" data-testid="ob-mtf-section">
            <span className="text-xs text-[var(--ink-soft,#4A5A52)]">Timeframes adicionales</span>
            {htfOptions.length === 0 ? (
              <p className="text-[10px] text-[var(--ink-soft,#4A5A52)] leading-relaxed">
                No hay timeframes superiores disponibles desde {chartTfLabel}.
              </p>
            ) : (
              <>
                <div className="grid grid-cols-4 gap-1">
                  {htfOptions.map((tf) => {
                    const selected = htfSelected.includes(tf)
                    const disabled = !selected && htfSelected.length >= MTF_MAX_ADDITIONAL
                    return (
                      <button
                        key={tf}
                        type="button"
                        disabled={disabled}
                        onClick={() => toggleHtf(tf)}
                        aria-pressed={selected}
                        data-tf={tf}
                        className={`px-1 py-1 text-[10px] font-medium rounded border transition-colors ${
                          selected
                            ? 'bg-[var(--paper,#FDFDFA)] border-[var(--gold,#B08D57)] text-[var(--green-deep,#0B3D2E)]'
                            : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-400 hover:bg-slate-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:hover:bg-white'
                        }`}
                      >
                        {mtfLabel(tf)}
                      </button>
                    )
                  })}
                </div>
                <p className="text-[10px] text-[var(--ink-soft,#4A5A52)] leading-relaxed">
                  Order Blocks de timeframes mayores: borde punteado y etiqueta con sufijo (p. ej. OB+ · 4H). Su ciclo de vida se evalúa en su propio timeframe. Máx. {MTF_MAX_ADDITIONAL}.
                </p>
              </>
            )}
          </div>
        </Section>

        <Section title="Visual" sectionKey="visual" config={cfg} onChange={onChange}>
          <p className="text-[10px] text-[var(--ink-soft,#4A5A52)] leading-relaxed">
            Bloques más pequeños que el umbral (% del precio medio de la zona) no se pintan ni etiquetan.
          </p>
          <SliderField label="Tamaño mínimo del bloque" value={cfg.minBlockSizePercent ?? 0} min={0} max={2} step={0.05} unit="%" onChange={(v) => update('minBlockSizePercent', v)} />
          <SliderField label="Longitud de caja" value={cfg.boxLengthBars ?? 50} min={10} max={200} step={1} unit=" barras" onChange={(v) => update('boxLengthBars', v)} />
          <p className="text-[10px] text-[var(--ink-soft,#4A5A52)] leading-relaxed">
            Longitud mínima visual de las zonas terminadas (mitigadas/breakers): del origen al evento, con mínimo N barras. Las activas siempre llegan a la última vela (estructura viva).
          </p>
        </Section>

        <Section title="Colores" sectionKey="colors" config={cfg} onChange={onChange}>
          <ColorPickerField label="OB alcista" value={cfg.colorBull ?? '#1B5E20'} onChange={(v) => update('colorBull', v)} />
          <ColorPickerField label="OB bajista" value={cfg.colorBear ?? '#B71C1C'} onChange={(v) => update('colorBear', v)} />
        </Section>

        <Section title="Estilo avanzado" sectionKey="style" config={cfg} onChange={onChange}>
          <SliderField label="Opacidad de relleno" value={cfg.fillOpacity ?? 0.14} min={0} max={0.5} step={0.01} onChange={(v) => update('fillOpacity', v)} />
          <SliderField label="Grosor de borde" value={cfg.borderWidth ?? 1.5} min={0} max={3} step={0.5} unit="px" onChange={(v) => update('borderWidth', v)} />
          <button
            onClick={resetStyle}
            className="w-full flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-medium rounded border border-[var(--hair,rgba(176,141,87,0.35))] bg-[var(--paper,#FDFDFA)] text-[var(--green-deep,#0B3D2E)] hover:bg-[var(--gold,#B08D57)] hover:text-white transition-colors"
          >
            <RotateCcw size={12} />
            Restablecer estilo Quantum
          </button>
        </Section>

      </div>
    </IndicatorConfigModal>
  )
}
